// src/services/learnerAnalyticsService.js
import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  setDoc, 
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  Timestamp
} from 'firebase/firestore';

/**
 * LEARNER ANALYTICS SERVICE
 * 
 * Data Structure:
 * 
 * pb-books/{bookId}/tasks/{taskId}/learnerProgress/{userId}
 *   - questions: { questionId: { box, attempts, correctAttempts, lastReviewed } }
 *   - currentStreak: number
 *   - longestStreak: number
 *   - lastStudyDate: string (YYYY-MM-DD)
 *   - totalStudyTime: number (seconds)
 *   - totalSessions: number
 *   - startedAt: timestamp
 *   - lastActivityAt: timestamp
 * 
 * pb-books/{bookId}/learnerSessions/{sessionId}
 *   - taskId: string
 *   - userId: string
 *   - startedAt: timestamp
 *   - completedAt: timestamp
 *   - duration: number (seconds)
 *   - questionsAnswered: number
 *   - correct: number
 *   - incorrect: number
 *   - accuracy: number (0-100)
 *   - boxChanges: { improved: number, declined: number, unchanged: number }
 *   - answers: [{ questionId, correct, timeSpent, boxBefore, boxAfter }]
 * 
 * pb-books/{bookId}/learnerDailyStats/{oderId}_{date}
 *   - oderId: string
 *   - odere: string (YYYY-MM-DD)
 *   - taskId: string
 *   - attempts: number
 *   - correct: number
 *   - incorrect: number
 *   - accuracy: number
 *   - sessions: number
 *   - studyTime: number (seconds)
 *   - boxImprovements: number
 *   - boxDeclines: number
 */

export const learnerAnalyticsService = {

  // ============================================================
  // SESSION TRACKING
  // ============================================================

  /**
   * Start a new study session
   */
  async startSession(bookId, taskId, userId) {
    try {
      const sessionsRef = collection(db, 'pb-books', bookId, 'learnerSessions');
      
      const sessionData = {
        taskId,
        userId,
        startedAt: serverTimestamp(),
        completedAt: null,
        duration: 0,
        questionsAnswered: 0,
        correct: 0,
        incorrect: 0,
        accuracy: 0,
        boxChanges: { improved: 0, declined: 0, unchanged: 0 },
        answers: [],
        status: 'active'
      };
      
      const docRef = await addDoc(sessionsRef, sessionData);
      return { id: docRef.id, ...sessionData };
    } catch (error) {
      console.error('Error starting session:', error);
      throw error;
    }
  },

  /**
   * Record an answer within a session
   */
  async recordAnswer(bookId, sessionId, answerData) {
    try {
      const sessionRef = doc(db, 'pb-books', bookId, 'learnerSessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        throw new Error('Session not found');
      }
      
      const session = sessionDoc.data();
      const answers = session.answers || [];
      
      answers.push({
        questionId: answerData.questionId,
        correct: answerData.correct,
        timeSpent: answerData.timeSpent || 0,
        boxBefore: answerData.boxBefore || 1,
        boxAfter: answerData.boxAfter || 1,
        answeredAt: new Date().toISOString()
      });
      
      const boxChange = answerData.boxAfter - answerData.boxBefore;
      const boxChanges = { ...session.boxChanges };
      if (boxChange > 0) boxChanges.improved++;
      else if (boxChange < 0) boxChanges.declined++;
      else boxChanges.unchanged++;
      
      const correct = session.correct + (answerData.correct ? 1 : 0);
      const incorrect = session.incorrect + (answerData.correct ? 0 : 1);
      const questionsAnswered = correct + incorrect;
      
      await updateDoc(sessionRef, {
        answers,
        questionsAnswered,
        correct,
        incorrect,
        accuracy: questionsAnswered > 0 ? Math.round((correct / questionsAnswered) * 100) : 0,
        boxChanges,
        duration: increment(answerData.timeSpent || 0)
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error recording answer:', error);
      throw error;
    }
  },

  /**
   * Complete a study session and update all analytics
   */
  async completeSession(bookId, sessionId, userId) {
    try {
      const sessionRef = doc(db, 'pb-books', bookId, 'learnerSessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        throw new Error('Session not found');
      }
      
      const session = sessionDoc.data();
      const now = new Date();
      const startedAt = session.startedAt?.toDate?.() || now;
      const duration = Math.round((now - startedAt) / 1000);
      
      // Update session as completed
      await updateDoc(sessionRef, {
        completedAt: serverTimestamp(),
        duration,
        status: 'completed'
      });
      
      // Update daily stats
      await this.updateDailyStats(bookId, session.taskId, userId, {
        attempts: session.questionsAnswered,
        correct: session.correct,
        incorrect: session.incorrect,
        studyTime: duration,
        boxImprovements: session.boxChanges?.improved || 0,
        boxDeclines: session.boxChanges?.declined || 0
      });
      
      // Update streak and progress summary
      await this.updateStreakAndSummary(bookId, session.taskId, userId, duration);
      
      return {
        sessionId,
        duration,
        questionsAnswered: session.questionsAnswered,
        correct: session.correct,
        accuracy: session.accuracy,
        boxChanges: session.boxChanges
      };
    } catch (error) {
      console.error('Error completing session:', error);
      throw error;
    }
  },

  /**
   * Record a quick session (for when session wasn't explicitly started)
   */
  async recordQuickSession(bookId, taskId, userId, sessionData) {
    try {
      const sessionsRef = collection(db, 'pb-books', bookId, 'learnerSessions');
      
      const answers = (sessionData.answers || []).map(a => ({
        questionId: a.questionId,
        correct: a.correct,
        timeSpent: a.timeSpent || 0,
        boxBefore: a.boxBefore || 1,
        boxAfter: a.boxAfter || 1,
        answeredAt: new Date().toISOString()
      }));
      
      const boxChanges = { improved: 0, declined: 0, unchanged: 0 };
      answers.forEach(a => {
        const change = a.boxAfter - a.boxBefore;
        if (change > 0) boxChanges.improved++;
        else if (change < 0) boxChanges.declined++;
        else boxChanges.unchanged++;
      });
      
      const correct = answers.filter(a => a.correct).length;
      const incorrect = answers.filter(a => !a.correct).length;
      const questionsAnswered = correct + incorrect;
      const duration = sessionData.duration || answers.reduce((sum, a) => sum + (a.timeSpent || 0), 0);
      
      const fullSessionData = {
        taskId,
        userId,
        startedAt: serverTimestamp(),
        completedAt: serverTimestamp(),
        duration,
        questionsAnswered,
        correct,
        incorrect,
        accuracy: questionsAnswered > 0 ? Math.round((correct / questionsAnswered) * 100) : 0,
        boxChanges,
        answers,
        status: 'completed'
      };
      
      const docRef = await addDoc(sessionsRef, fullSessionData);
      
      // Update daily stats
      await this.updateDailyStats(bookId, taskId, userId, {
        attempts: questionsAnswered,
        correct,
        incorrect,
        studyTime: duration,
        boxImprovements: boxChanges.improved,
        boxDeclines: boxChanges.declined
      });
      
      // Update streak and summary
      await this.updateStreakAndSummary(bookId, taskId, userId, duration);
      
      return { id: docRef.id, ...fullSessionData };
    } catch (error) {
      console.error('Error recording quick session:', error);
      throw error;
    }
  },

  // ============================================================
  // DAILY STATS
  // ============================================================

  /**
   * Update daily statistics
   */
  async updateDailyStats(bookId, taskId, userId, stats) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const docId = `${userId}_${taskId}_${today}`;
      const dailyRef = doc(db, 'pb-books', bookId, 'learnerDailyStats', docId);
      const dailyDoc = await getDoc(dailyRef);
      
      if (dailyDoc.exists()) {
        await updateDoc(dailyRef, {
          attempts: increment(stats.attempts || 0),
          correct: increment(stats.correct || 0),
          incorrect: increment(stats.incorrect || 0),
          sessions: increment(1),
          studyTime: increment(stats.studyTime || 0),
          boxImprovements: increment(stats.boxImprovements || 0),
          boxDeclines: increment(stats.boxDeclines || 0),
          updatedAt: serverTimestamp()
        });
      } else {
        const accuracy = stats.attempts > 0 
          ? Math.round((stats.correct / stats.attempts) * 100) 
          : 0;
        
        await setDoc(dailyRef, {
          userId,
          taskId,
          date: today,
          attempts: stats.attempts || 0,
          correct: stats.correct || 0,
          incorrect: stats.incorrect || 0,
          accuracy,
          sessions: 1,
          studyTime: stats.studyTime || 0,
          boxImprovements: stats.boxImprovements || 0,
          boxDeclines: stats.boxDeclines || 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error updating daily stats:', error);
      // Don't throw - daily stats update shouldn't break flow
      return { success: false, error };
    }
  },

  /**
   * Get daily stats for a date range
   */
  async getDailyStats(bookId, taskId, userId, startDate, endDate) {
    try {
      const dailyRef = collection(db, 'pb-books', bookId, 'learnerDailyStats');
      const q = query(
        dailyRef,
        where('userId', '==', userId),
        where('taskId', '==', taskId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting daily stats:', error);
      return [];
    }
  },

  // ============================================================
  // STREAK & SUMMARY TRACKING
  // ============================================================

  /**
   * Update streak and summary in learnerProgress
   */
  async updateStreakAndSummary(bookId, taskId, userId, studyTime = 0) {
    try {
      const progressRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress', userId);
      const progressDoc = await getDoc(progressRef);
      
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      
      if (progressDoc.exists()) {
        const data = progressDoc.data();
        const lastStudyDate = data.lastStudyDate;
        let currentStreak = data.currentStreak || 0;
        let longestStreak = data.longestStreak || 0;
        
        if (lastStudyDate === today) {
          // Already studied today, just update time
          await updateDoc(progressRef, {
            totalStudyTime: increment(studyTime),
            totalSessions: increment(1),
            lastActivityAt: serverTimestamp()
          });
        } else if (lastStudyDate === yesterday) {
          // Consecutive day - increment streak
          currentStreak += 1;
          longestStreak = Math.max(longestStreak, currentStreak);
          
          await updateDoc(progressRef, {
            currentStreak,
            longestStreak,
            lastStudyDate: today,
            totalStudyTime: increment(studyTime),
            totalSessions: increment(1),
            lastActivityAt: serverTimestamp()
          });
        } else {
          // Streak broken - reset to 1
          await updateDoc(progressRef, {
            currentStreak: 1,
            longestStreak: Math.max(longestStreak, 1),
            lastStudyDate: today,
            totalStudyTime: increment(studyTime),
            totalSessions: increment(1),
            lastActivityAt: serverTimestamp()
          });
        }
      } else {
        // First time - initialize
        await setDoc(progressRef, {
          questions: {},
          currentStreak: 1,
          longestStreak: 1,
          lastStudyDate: today,
          totalStudyTime: studyTime,
          totalSessions: 1,
          startedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp()
        }, { merge: true });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error updating streak:', error);
      return { success: false };
    }
  },

  // ============================================================
  // ANALYTICS QUERIES
  // ============================================================

  /**
   * Get learner's performance trend over time
   */
  async getPerformanceTrend(bookId, taskId, userId, days = 30) {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
      
      const dailyStats = await this.getDailyStats(bookId, taskId, userId, startDate, endDate);
      
      // Fill in missing days with zeros
      const filledStats = [];
      const currentDate = new Date(startDate);
      const end = new Date(endDate);
      
      while (currentDate <= end) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const dayStats = dailyStats.find(d => d.date === dateStr);
        
        filledStats.push({
          date: dateStr,
          attempts: dayStats?.attempts || 0,
          correct: dayStats?.correct || 0,
          accuracy: dayStats?.accuracy || 0,
          sessions: dayStats?.sessions || 0,
          studyTime: dayStats?.studyTime || 0,
          boxImprovements: dayStats?.boxImprovements || 0,
          boxDeclines: dayStats?.boxDeclines || 0,
          netBoxChange: (dayStats?.boxImprovements || 0) - (dayStats?.boxDeclines || 0)
        });
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Calculate trend metrics
      const totalAttempts = filledStats.reduce((sum, d) => sum + d.attempts, 0);
      const totalCorrect = filledStats.reduce((sum, d) => sum + d.correct, 0);
      const totalStudyTime = filledStats.reduce((sum, d) => sum + d.studyTime, 0);
      const activeDays = filledStats.filter(d => d.attempts > 0).length;
      
      // Compare first half vs second half for trend direction
      const midpoint = Math.floor(filledStats.length / 2);
      const firstHalf = filledStats.slice(0, midpoint);
      const secondHalf = filledStats.slice(midpoint);
      
      const firstHalfAccuracy = this.calculateAverageAccuracy(firstHalf);
      const secondHalfAccuracy = this.calculateAverageAccuracy(secondHalf);
      const trendDirection = secondHalfAccuracy - firstHalfAccuracy;
      
      return {
        dailyStats: filledStats,
        summary: {
          totalAttempts,
          totalCorrect,
          overallAccuracy: totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0,
          totalStudyTime,
          activeDays,
          avgStudyTimePerDay: activeDays > 0 ? Math.round(totalStudyTime / activeDays) : 0,
          trendDirection, // positive = improving, negative = declining
          isImproving: trendDirection > 0
        }
      };
    } catch (error) {
      console.error('Error getting performance trend:', error);
      return { dailyStats: [], summary: {} };
    }
  },

  calculateAverageAccuracy(stats) {
    const withAttempts = stats.filter(d => d.attempts > 0);
    if (withAttempts.length === 0) return 0;
    return withAttempts.reduce((sum, d) => sum + d.accuracy, 0) / withAttempts.length;
  },

  /**
   * Get learner's session history
   */
  async getSessionHistory(bookId, taskId, userId, limitCount = 20) {
    try {
      const sessionsRef = collection(db, 'pb-books', bookId, 'learnerSessions');
      const q = query(
        sessionsRef,
        where('taskId', '==', taskId),
        where('userId', '==', userId),
        where('status', '==', 'completed'),
        orderBy('completedAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting session history:', error);
      return [];
    }
  },

  /**
   * Get learner's comprehensive analytics
   */
  async getLearnerAnalytics(bookId, taskId, userId) {
    try {
      // Get progress data
      const progressRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress', userId);
      const progressDoc = await getDoc(progressRef);
      const progress = progressDoc.exists() ? progressDoc.data() : null;
      
      // Get recent sessions
      const sessions = await this.getSessionHistory(bookId, taskId, userId, 10);
      
      // Get performance trend (last 30 days)
      const trend = await this.getPerformanceTrend(bookId, taskId, userId, 30);
      
      // Calculate question-level stats
      const questionStats = this.calculateQuestionStats(progress?.questions || {});
      
      return {
        progress: {
          currentStreak: progress?.currentStreak || 0,
          longestStreak: progress?.longestStreak || 0,
          totalSessions: progress?.totalSessions || 0,
          totalStudyTime: progress?.totalStudyTime || 0,
          lastStudyDate: progress?.lastStudyDate,
          startedAt: progress?.startedAt
        },
        questionStats,
        recentSessions: sessions,
        trend: trend.summary,
        dailyStats: trend.dailyStats
      };
    } catch (error) {
      console.error('Error getting learner analytics:', error);
      throw error;
    }
  },

  /**
   * Calculate comprehensive question stats from progress
   */
  calculateQuestionStats(questions) {
    // Flatten nested structures
    const flatQuestions = {};
    Object.entries(questions || {}).forEach(([key, value]) => {
      if (value && typeof value.box === 'number') {
        flatQuestions[key] = value;
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (subValue && typeof subValue.box === 'number') {
            flatQuestions[subKey] = subValue;
          }
        });
      }
    });
    
    const questionsArray = Object.values(flatQuestions);
    if (questionsArray.length === 0) {
      return {
        total: 0,
        totalAttempts: 0,
        totalCorrect: 0,
        accuracy: 0,
        mastered: 0,
        learning: 0,
        struggling: 0,
        masteryPercent: 0,
        avgBox: 0,
        boxDistribution: [0, 0, 0, 0, 0]
      };
    }
    
    const totalAttempts = questionsArray.reduce((sum, q) => sum + (q.attempts || 0), 0);
    const totalCorrect = questionsArray.reduce((sum, q) => sum + (q.correctAttempts || 0), 0);
    const mastered = questionsArray.filter(q => q.box >= 4).length;
    const learning = questionsArray.filter(q => q.box === 2 || q.box === 3).length;
    const struggling = questionsArray.filter(q => q.box === 1).length;
    
    const boxDistribution = [0, 0, 0, 0, 0];
    questionsArray.forEach(q => {
      const box = Math.min(Math.max((q.box || 1) - 1, 0), 4);
      boxDistribution[box]++;
    });
    
    const avgBox = questionsArray.reduce((sum, q) => sum + (q.box || 1), 0) / questionsArray.length;
    
    return {
      total: questionsArray.length,
      totalAttempts,
      totalCorrect,
      accuracy: totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0,
      mastered,
      learning,
      struggling,
      masteryPercent: Math.round((mastered / questionsArray.length) * 100),
      avgBox: avgBox.toFixed(1),
      boxDistribution
    };
  },

  // ============================================================
  // TASK-LEVEL ANALYTICS (For owners/admins)
  // ============================================================

  /**
   * Get aggregate analytics for all learners on a task
   */
  async getTaskAnalytics(bookId, taskId) {
    try {
      // Get all learner progress
      const progressRef = collection(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress');
      const progressSnapshot = await getDocs(progressRef);
      
      const learners = progressSnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data()
      }));
      
      if (learners.length === 0) {
        return {
          totalLearners: 0,
          activeLearners: 0,
          avgMastery: 0,
          avgAccuracy: 0,
          totalSessions: 0,
          avgStreak: 0,
          learnerBreakdown: { struggling: 0, learning: 0, proficient: 0, mastered: 0 }
        };
      }
      
      // Calculate per-learner stats
      const learnerStats = learners.map(l => {
        const stats = this.calculateQuestionStats(l.questions);
        return {
          userId: l.userId,
          ...stats,
          currentStreak: l.currentStreak || 0,
          totalSessions: l.totalSessions || 0,
          totalStudyTime: l.totalStudyTime || 0,
          lastStudyDate: l.lastStudyDate
        };
      });
      
      // Aggregate
      const totalLearners = learnerStats.length;
      const avgMastery = Math.round(learnerStats.reduce((sum, l) => sum + l.masteryPercent, 0) / totalLearners);
      const avgAccuracy = Math.round(learnerStats.reduce((sum, l) => sum + l.accuracy, 0) / totalLearners);
      const totalSessions = learnerStats.reduce((sum, l) => sum + l.totalSessions, 0);
      const avgStreak = Math.round(learnerStats.reduce((sum, l) => sum + l.currentStreak, 0) / totalLearners);
      
      // Active in last 7 days
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
      const activeLearners = learnerStats.filter(l => l.lastStudyDate >= weekAgo).length;
      
      // Breakdown by mastery level
      const learnerBreakdown = {
        struggling: learnerStats.filter(l => l.masteryPercent < 25).length,
        learning: learnerStats.filter(l => l.masteryPercent >= 25 && l.masteryPercent < 50).length,
        proficient: learnerStats.filter(l => l.masteryPercent >= 50 && l.masteryPercent < 80).length,
        mastered: learnerStats.filter(l => l.masteryPercent >= 80).length
      };
      
      return {
        totalLearners,
        activeLearners,
        avgMastery,
        avgAccuracy,
        totalSessions,
        avgStreak,
        learnerBreakdown,
        learners: learnerStats,
        totalAttempts: learnerStats.reduce((sum, l) => sum + l.totalAttempts, 0),
        totalCorrect: learnerStats.reduce((sum, l) => sum + l.totalCorrect, 0)
      };
    } catch (error) {
      console.error('Error getting task analytics:', error);
      throw error;
    }
  },

  /**
   * Get question-level analytics across all learners
   */
  async getQuestionAnalytics(bookId, taskId) {
    try {
      const progressRef = collection(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress');
      const snapshot = await getDocs(progressRef);
      
      const questionStats = {};
      
      snapshot.docs.forEach(doc => {
        const progress = doc.data();
        
        Object.entries(progress.questions || {}).forEach(([key, value]) => {
          // Check if this is a direct question entry (has 'box' as number)
          if (value && typeof value.box === 'number') {
            // Simple question - key directly by question ID
            if (!questionStats[key]) {
              questionStats[key] = {
                questionId: key,
                learnerCount: 0,
                totalAttempts: 0,
                totalCorrect: 0,
                boxes: []
              };
            }
            questionStats[key].learnerCount++;
            questionStats[key].totalAttempts += value.attempts || 0;
            questionStats[key].totalCorrect += value.correctAttempts || 0;
            questionStats[key].boxes.push(value.box || 1);
            
          } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            // Nested structure - aggregate under the PARENT key (subQuestion ID like sq_xxx)
            if (!questionStats[key]) {
              questionStats[key] = {
                questionId: key,
                learnerCount: 0,
                totalAttempts: 0,
                totalCorrect: 0,
                boxes: []
              };
            }
            
            // Aggregate all nested entries
            Object.entries(value).forEach(([subKey, subValue]) => {
              if (subValue && typeof subValue.box === 'number') {
                questionStats[key].learnerCount++;
                questionStats[key].totalAttempts += subValue.attempts || 0;
                questionStats[key].totalCorrect += subValue.correctAttempts || 0;
                questionStats[key].boxes.push(subValue.box || 1);
              }
            });
          }
        });
      });
      
      // Calculate derived stats
      Object.values(questionStats).forEach(stat => {
        stat.accuracy = stat.totalAttempts > 0 
          ? Math.round((stat.totalCorrect / stat.totalAttempts) * 100) 
          : 0;
        stat.avgBox = stat.boxes.length > 0
          ? (stat.boxes.reduce((a, b) => a + b, 0) / stat.boxes.length).toFixed(1)
          : '1.0';
        stat.difficulty = stat.accuracy < 40 ? 'hard' : stat.accuracy < 70 ? 'medium' : 'easy';
        
        stat.boxDistribution = [0, 0, 0, 0, 0];
        stat.boxes.forEach(box => {
          const idx = Math.min(Math.max(box - 1, 0), 4);
          stat.boxDistribution[idx]++;
        });
        
        delete stat.boxes;
      });
      
      return questionStats;
    } catch (error) {
      console.error('Error getting question analytics:', error);
      return {};
    }
  },

  // ============================================================
  // COMPARISON & INSIGHTS
  // ============================================================

  /**
   * Compare learner to peer average
   */
  async getLearnerComparison(bookId, taskId, userId) {
    try {
      const [learnerAnalytics, taskAnalytics] = await Promise.all([
        this.getLearnerAnalytics(bookId, taskId, userId),
        this.getTaskAnalytics(bookId, taskId)
      ]);
      
      return {
        learner: learnerAnalytics,
        peerAverage: {
          mastery: taskAnalytics.avgMastery,
          accuracy: taskAnalytics.avgAccuracy,
          streak: taskAnalytics.avgStreak
        },
        comparison: {
          masteryVsPeer: learnerAnalytics.questionStats.masteryPercent - taskAnalytics.avgMastery,
          accuracyVsPeer: learnerAnalytics.questionStats.accuracy - taskAnalytics.avgAccuracy,
          streakVsPeer: learnerAnalytics.progress.currentStreak - taskAnalytics.avgStreak
        },
        percentile: this.calculatePercentile(learnerAnalytics.questionStats.masteryPercent, taskAnalytics.learners)
      };
    } catch (error) {
      console.error('Error getting learner comparison:', error);
      throw error;
    }
  },

  calculatePercentile(learnerMastery, allLearners) {
    if (!allLearners || allLearners.length === 0) return 50;
    
    const below = allLearners.filter(l => l.masteryPercent < learnerMastery).length;
    return Math.round((below / allLearners.length) * 100);
  },

  /**
   * Get improvement insights for a learner
   */
  async getImprovementInsights(bookId, taskId, userId) {
    try {
      const trend = await this.getPerformanceTrend(bookId, taskId, userId, 14);
      const sessions = await this.getSessionHistory(bookId, taskId, userId, 5);
      
      const insights = [];
      
      // Trend-based insights
      if (trend.summary.isImproving) {
        insights.push({
          type: 'positive',
          icon: 'TrendingUp',
          message: `Great progress! Your accuracy improved by ${Math.abs(Math.round(trend.summary.trendDirection))}% over the past 2 weeks.`
        });
      } else if (trend.summary.trendDirection < -5) {
        insights.push({
          type: 'warning',
          icon: 'TrendingDown',
          message: `Your accuracy dropped by ${Math.abs(Math.round(trend.summary.trendDirection))}%. Consider reviewing difficult questions.`
        });
      }
      
      // Consistency insights
      if (trend.summary.activeDays >= 5) {
        insights.push({
          type: 'positive',
          icon: 'Calendar',
          message: `Excellent consistency! You studied ${trend.summary.activeDays} days in the last 2 weeks.`
        });
      } else if (trend.summary.activeDays < 3) {
        insights.push({
          type: 'suggestion',
          icon: 'Calendar',
          message: 'Try to study more regularly. Even 10 minutes daily helps retention.'
        });
      }
      
      // Session-based insights
      if (sessions.length >= 2) {
        const recentAccuracy = sessions[0]?.accuracy || 0;
        const previousAccuracy = sessions[1]?.accuracy || 0;
        
        if (recentAccuracy > previousAccuracy + 10) {
          insights.push({
            type: 'positive',
            icon: 'Zap',
            message: `Strong session! You scored ${recentAccuracy - previousAccuracy}% higher than your previous session.`
          });
        }
      }
      
      return insights;
    } catch (error) {
      console.error('Error getting improvement insights:', error);
      return [];
    }
  }
};